import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { injectIntl, useIntl } from 'react-intl';
import MaintenanceKanbanCard from '../../Maintenance/components/KanbanCards';
import {
  selectTasks,
  selectCurrentEmployee,
  selectTaskPrefixes,
  selectTaskLocations,
} from '../../reducer';
import { selectEmployees } from '../../../employees/reducer';
import { selectSite } from '../../../../store/authReducer';
import { selectSitesBasic } from '../../../site-selection/reducer';
import * as helpers from '../QuickTasks/helpers';
import { KANBAN_COLUMNS, DATE_FORMATS, PRIORITY_VALUE_TO_LABEL } from '../QuickTasks/constants';
import moment from 'moment';
import { updateTaskCompletionStatus as updateTaskCompletionStatusAction } from '../../../overview/actions';
import { TaskType, ProjectStatus } from '../../models';
import { fetchTasks, getLastTasksQuery, getTaskLocations } from '../../actions';
import {
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import '../../../../styles/_maintenance-cards.scss';

// Statuses that predate the New / On Hold / In Progress / Completed vocabulary can
// still come back on older completions. Fold them onto the closest current column
// rather than dropping them into an unrelated one.
const LEGACY_STATUS_COLUMNS: Record<string, string> = {
  Planning: ProjectStatus.InProgress,
  Review: ProjectStatus.InProgress,
};

type UpdateTaskCompletionArgs = {
  options: { siteId: number };
  taskIds: number[];
  status: string;
  employeeId: number;
  notes: any;
  callback?: () => void;
};

type Props = {
  tasks: any[];
  employees: any[];
  taskPrefixes: any[];
  taskLocations: any[];
  sites: any[];
  site: number;
  currentEmployee?: number;
  intl: any;
  onUpdateTaskCompletionStatus: (args: UpdateTaskCompletionArgs) => any;
  onOpenTask?: (task: any) => void;
  filters?: any;
  refreshTasks?: () => void;
  hideCompleted?: boolean;
  showOnHold?: boolean;
  fetchTaskLocations?: () => void;
};

const WorkOrdersKanban: React.FC<Props> = ({
  tasks = [],
  employees = [],
  taskPrefixes = [],
  taskLocations = [],
  sites = [],
  site,
  currentEmployee,
  intl,
  onUpdateTaskCompletionStatus,
  onOpenTask,
  filters,
  refreshTasks,
  hideCompleted: hideCompletedProp,
  showOnHold = false,
  fetchTaskLocations,
}) => {
  useEffect(() => {
    if (fetchTaskLocations && !taskLocations.length) {
      fetchTaskLocations();
    }
  }, [fetchTaskLocations, taskLocations.length]);
  const projectTasks = useMemo(() => {
    const base = (tasks || []).filter((t) => {
      const typeVal = t?.type;
      return typeVal === TaskType.Project || typeVal === 'Project' || typeVal === 1;
    });

    const ext = filters || {};
    const priorityFilter: string[] = Array.isArray(ext.priorityFilter) ? ext.priorityFilter : [];

    const nameQuery: string = ext.nameQuery ?? '';
    const detailsQuery: string = ext.detailsQuery ?? '';
    const dateQuery: string = ext.dateQuery ?? '';
    const createdDateQuery: string = ext.createdDateQuery ?? '';
    const memberFilter: (string | number)[] = Array.isArray(ext.memberFilter)
      ? ext.memberFilter
      : [];
    const prefixFilterSrc: any[] = Array.isArray(ext.prefixFilter) ? ext.prefixFilter : [];
    const prefixFilterVal = prefixFilterSrc.map(String);

    const priorityText = (p: any) => PRIORITY_VALUE_TO_LABEL[Number(p ?? -1)];

    return base.filter((t) => {
      const passesPriority =
        priorityFilter.length === 0 || priorityFilter.includes(priorityText(t?.priority));
      const passesName = helpers.passesName(t, nameQuery);
      const passesDetails = helpers.passesDetails(t, detailsQuery);
      const passesDate = helpers.passesDate(t, dateQuery);
      const passesCreatedDate = helpers.passesCreatedDate(t, createdDateQuery);
      const passesMember = helpers.passesMember(t, memberFilter);
      const passesPrefix = helpers.passesPrefix(t, prefixFilterVal);
      return (
        passesPriority &&
        passesName &&
        passesDetails &&
        passesDate &&
        passesCreatedDate &&
        passesMember &&
        passesPrefix
      );
    });
  }, [tasks, filters]);

  const grouped = useMemo(() => {
    const init = (KANBAN_COLUMNS || []).reduce((acc: Record<string, any[]>, col) => {
      acc[col.title] = [];
      return acc;
    }, {} as Record<string, any[]>);

    projectTasks.forEach((task) => {
      const statusText = helpers.normalizeProjectStatus(
        task?.latestCompletion?.status != null
          ? String(task.latestCompletion.status)
          : String(task?.desiredStatus || task?.desired_status || ProjectStatus.New)
      );
      const key = init[statusText]
        ? statusText
        : LEGACY_STATUS_COLUMNS[statusText] ?? ProjectStatus.New;
      init[key].push(task);
    });

    return init;
  }, [projectTasks]);

  const visibleColumns = useMemo(() => {
    const hideCompleted = Boolean(hideCompletedProp);
    return (KANBAN_COLUMNS || []).filter((c) => {
      if (hideCompleted && c.title === ProjectStatus.Completed) return false;
      if (!showOnHold && c.title === ProjectStatus.OnHold) return false;
      return true;
    });
  }, [hideCompletedProp, showOnHold]);

  const handleDropTo = (taskId: string | number, nextStatusDisplay: string) => {
    const employeeId = currentEmployee;
    if (!employeeId) return;
    const apiStatus = helpers.mapDisplayStatusToApiStatus(nextStatusDisplay);
    onUpdateTaskCompletionStatus({
      options: { siteId: site },
      taskIds: [Number(taskId)],
      status: apiStatus,
      employeeId,
      notes: null,
      callback: () => refreshTasks && refreshTasks(),
    });
  };

  const handleComplete = (taskId: string | number) => handleDropTo(taskId, 'Completed');

  const getEmployeeName = (employeeId?: number | string) => {
    const emp = employees.find((e: any) => String(e.id) === String(employeeId));
    if (!emp) return undefined;
    const first = emp.firstName || '';
    const last = emp.lastName || '';
    const fullName = [first, last].filter(Boolean).join(' ').trim();
    return fullName || undefined;
  };

  const renderCard = (columnId: string, colKey: string, task: any) => {
    const nameOnly = task?.name || intl.formatMessage({ id: 'notAvailable' });
    const prefix = (taskPrefixes || []).find(
      (p: any) => String(p?.type) === String(task?.prefixId)
    );
    const displayName = prefix && prefix.message ? `${prefix.message} ${nameOnly}` : nameOnly;
    const assigneeName = getEmployeeName(task?.employeeId);
    const location = (taskLocations || []).find(
      (loc: any) => String(loc?.type) === String(task?.locationId)
    );
    const locationName = location?.message;
    const siteObj = (sites || []).find((s: any) => String(s?.id) === String(task?.siteId));
    const siteName = siteObj?.externalId || siteObj?.name;
    return (
      <MaintenanceKanbanCard
        key={task.id}
        columnId={columnId}
        item={{
          id: String(task.id),
          // label: task.name || intl.formatMessage({ id: 'notAvailable' }),
          label: displayName,
          subText: helpers.stripHtml(task?.description || ''),
          dueDate: task?.start ? moment(task.start).format(DATE_FORMATS.MDY) : undefined,
          status: colKey as any,
          urgent: PRIORITY_VALUE_TO_LABEL[Number(task?.priority ?? -1)] === 'Urgent',
          priority: task?.priority ?? 0,
          location: locationName,
          site: siteName,
        }}
        onDropOver={(target, source) => {
          if (source?.srcId && target?.overColumnId) {
            handleDropTo(source.srcId, target.overColumnId);
          }
        }}
        onFlagClick={() => {}}
        onCompleteClick={(fromCol, itemId) => handleComplete(itemId)}
        onOpen={() => onOpenTask && onOpenTask(task)}
        assigneeName={assigneeName}
        assigneeUnassigned={!assigneeName}
      />
    );
  };

  return (
    <div
      className="maintenance-board"
      data-columns={visibleColumns.length}
      data-show-on-hold={showOnHold}
      style={{
        gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(220px, 1fr))`,
        gap: '12px',
        padding: '8px 6px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {visibleColumns.map((col) => (
        <WorkOrdersKanbanColumn
          key={col.id}
          columnId={col.id}
          title={col.title}
          onDropOnColumn={(payload) => {
            if (payload?.srcId) {
              handleDropTo(payload.srcId, col.title);
            }
          }}
        >
          {(grouped[col.title] || []).map((t) => renderCard(col.id, col.title, t))}
        </WorkOrdersKanbanColumn>
      ))}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  tasks: selectTasks(state),
  employees: selectEmployees(state),
  taskPrefixes: selectTaskPrefixes(state),
  taskLocations: selectTaskLocations(state),
  sites: selectSitesBasic(state),
  site: Number(selectSite(state)),
  currentEmployee: selectCurrentEmployee(state),
});

const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators(
    {
      onUpdateTaskCompletionStatus: updateTaskCompletionStatusAction,
      refreshTasks: () => fetchTasks(getLastTasksQuery()),
      fetchTaskLocations: getTaskLocations,
    },
    dispatch
  );

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(WorkOrdersKanban) as React.ComponentType<any>;

type ColumnDropPayload = { srcId: string; fromColumnId?: string };

// Exported so v2 (TaskTemplateProjectsKanban) can compose the same column / drop-target
// without duplicating the drag-state plumbing. Re-export of the legacy column
// keeps a single source of truth for the drag-over visual states.
export const WorkOrdersKanbanColumn: React.FC<{
  columnId: string;
  title: string;
  onDropOnColumn: (payload: ColumnDropPayload) => void;
  children: React.ReactNode;
}> = ({ columnId, title, onDropOnColumn, children }) => {
  const colRef = useRef<HTMLDivElement | null>(null);
  const [isOverColumn, setIsOverColumn] = useState(false);
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const intl = useIntl();

  const titleStyle: React.CSSProperties = useMemo(() => {
    const map: Record<string, { bg: string; fg: string }> = {
      [ProjectStatus.New]: { bg: '#d1d8ff', fg: '#5c75ff' },
      [ProjectStatus.OnHold]: { bg: '#ffe8d1', fg: '#f7830f' },
      [ProjectStatus.InProgress]: { bg: '#ffd1f7', fg: '#fc52df' },
      [ProjectStatus.Completed]: { bg: '#ccffe2', fg: '#34c673' },
    };
    const m = map[title] || { bg: '#f1f5f9', fg: '#334155' };
    return { background: m.bg, color: m.fg };
  }, [title]);

  useEffect(() => {
    const handleDragStart = () => {
      setIsDraggingCard(true);
    };

    const handleDragOver = (event: Event) => {
      const customEvent = event as CustomEvent;
      const hoveredColumnId = customEvent.detail?.columnId;
      setIsOverColumn(hoveredColumnId === columnId);
    };

    const handleDragEnd = () => {
      setIsDraggingCard(false);
      setIsOverColumn(false);
    };

    window.addEventListener('kanban-drag-start', handleDragStart);
    window.addEventListener('kanban-drag-over', handleDragOver);
    window.addEventListener('kanban-drag-end', handleDragEnd);

    return () => {
      window.removeEventListener('kanban-drag-start', handleDragStart);
      window.removeEventListener('kanban-drag-over', handleDragOver);
      window.removeEventListener('kanban-drag-end', handleDragEnd);
    };
  }, [columnId]);

  return (
    <div
      className={`maintenance-column maintenance-column--${columnId} ${
        isOverColumn ? 'is-over' : ''
      } ${isDraggingCard ? 'is-dragging-card' : ''}`}
    >
      {isDraggingCard && (
        <div className="maintenance-column__overlay" style={{ pointerEvents: 'none' }}>
          <div className="maintenance-column__overlay-msg">
            {isOverColumn
              ? intl.formatMessage(
                  { id: 'releaseToDropInto', defaultMessage: 'Release To Drop Into {column}' },
                  {
                    column: intl.formatMessage({
                      id: title.toLowerCase().replace(/\s+/g, ''),
                      defaultMessage: title,
                    }),
                  }
                )
              : intl.formatMessage({
                  id: 'dragHereToMoveCard',
                  defaultMessage: 'Drag Here To Move Card',
                })}
          </div>
        </div>
      )}
      <div className="maintenance-column__title" style={titleStyle}>
        {intl.formatMessage({ id: title.toLowerCase().replace(/\s+/g, ''), defaultMessage: title })}
      </div>
      <div ref={colRef} className="maintenance-column__body">
        {children}
      </div>
    </div>
  );
};
